import React, { useEffect, useState, Component } from 'react';
import { useSearchParams } from 'react-router-dom';
import { confirmPasswordReset, applyActionCode } from 'firebase/auth';
import { auth } from '../../firebase';
import { AlertCircleIcon, CheckCircleIcon, EyeIcon, EyeOffIcon, LockIcon, MailCheckIcon, LoaderIcon } from 'lucide-react';
// MODULE-LEVEL LOG: Proves the file is included in the build
console.log('[ResetPasswordPage] Module loaded — build marker: RPP_2025_03_23_B');
// Error boundary to catch any render crashes
class ActionPageErrorBoundary extends Component<{
  children: React.ReactNode;
}, {
  hasError: boolean;
  error: string;
}> {
  constructor(props: {
    children: React.ReactNode;
  }) {
    super(props);
    this.state = {
      hasError: false,
      error: ''
    };
  }
  static getDerivedStateFromError(error: Error) {
    return {
      hasError: true,
      error: error.message
    };
  }
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[ResetPasswordPage] Render crash:', error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return <div style={styles.pageWrapper} data-id="element-6691">
          <div style={styles.card} data-id="element-6692">
            <div style={styles.cardInner} data-id="element-6693">
              <h2 style={{
              fontSize: '1.5rem',
              fontWeight: 'bold',
              color: '#111827',
              marginBottom: '1rem'
            }} data-id="element-6694">
                Something went wrong
              </h2>
              <p style={{
              color: '#6B7280',
              marginBottom: '1rem'
            }} data-id="element-6695">
                Error: {this.state.error}
              </p>
              <a href="/forgot-password" style={styles.primaryButton} data-id="element-6696">
                Request New Link
              </a>
            </div>
          </div>
        </div>;
    }
    return this.props.children;
  }
}
// ============================================================
// EMAIL VERIFICATION FLOW
// ============================================================
const EmailVerificationFlow = ({
  oobCode
}: {
  oobCode: string;
}) => {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [error, setError] = useState('');
  useEffect(() => {
    const verifyEmail = async () => {
      try {
        console.log('[ResetPasswordPage] Verifying email with oobCode...');
        await applyActionCode(auth, oobCode);
        console.log('[ResetPasswordPage] Email verified successfully!');
        // Refresh user if logged in
        if (auth.currentUser) {
          await auth.currentUser.reload();
          await auth.currentUser.getIdToken(true);
          console.log('[ResetPasswordPage] User token refreshed after verification');
        }
        setStatus('success');
      } catch (err: any) {
        console.error('[ResetPasswordPage] Email verification error:', err);
        if (err.code === 'auth/expired-action-code') {
          setError('This verification link has expired. Please request a new one.');
        } else if (err.code === 'auth/invalid-action-code') {
          setError('This verification link is invalid or has already been used.');
        } else {
          setError('Failed to verify email. Please try again.');
        }
        setStatus('error');
      }
    };
    verifyEmail();
  }, [oobCode]);
  // Loading state
  if (status === 'loading') {
    return <div style={styles.pageWrapper} data-id="element-6697">
        <div style={styles.card} data-id="element-6698">
          <div style={styles.cardInner} data-id="element-6699">
            <div style={{
            textAlign: 'center'
          }} data-id="element-6700">
              <div style={{
              ...styles.iconCircle,
              backgroundColor: '#DBEAFE'
            }} data-id="element-6701">
                <LoaderIcon style={{
                width: '2rem',
                height: '2rem',
                color: '#2563EB',
                animation: 'spin 1s linear infinite'
              }} data-id="element-6702" />
              </div>
              <h2 style={styles.heading} data-id="element-6703">Verifying Your Email</h2>
              <p style={styles.subtitle} data-id="element-6704">
                Please wait while we verify your email address...
              </p>
            </div>
          </div>
        </div>
        <style data-id="element-6705">{`{spinnerKeyframes}`}</style>
      </div>;
  }
  // Success state
  if (status === 'success') {
    return <div style={styles.pageWrapper} data-id="element-6706">
        <div style={styles.card} data-id="element-6707">
          <div style={styles.cardInner} data-id="element-6708">
            <div style={{
            textAlign: 'center'
          }} data-id="element-6709">
              <div style={{
              ...styles.iconCircle,
              backgroundColor: '#D1FAE5'
            }} data-id="element-6710">
                <MailCheckIcon style={{
                width: '2rem',
                height: '2rem',
                color: '#059669'
              }} data-id="element-6711" />
              </div>
              <h2 style={styles.heading} data-id="element-6712">Email Verified!</h2>
              <p style={styles.subtitle} data-id="element-6713">
                Your email has been successfully verified.
              </p>
            </div>

            <div style={{
            marginTop: '1.5rem',
            padding: '1rem',
            backgroundColor: '#F0FDF4',
            borderLeft: '4px solid #22C55E',
            color: '#166534',
            display: 'flex',
            alignItems: 'flex-start',
            borderRadius: '0 0.5rem 0.5rem 0'
          }} data-id="element-6714">
              <CheckCircleIcon style={{
              width: '1.25rem',
              height: '1.25rem',
              marginRight: '0.5rem',
              flexShrink: 0,
              marginTop: '2px'
            }} data-id="element-6715" />
              <span data-id="element-6716">
                Your email has been successfully verified. You can now sign in
                to your account.
              </span>
            </div>

            <a href="/teacher-login" style={{
            ...styles.primaryButton,
            marginTop: '1.5rem'
          }} data-id="element-6717">
              Go to Sign In
            </a>

            <div style={styles.footer} data-id="element-6718">
              <p style={{
              color: '#6B7280'
            }} data-id="element-6719">
                Need help?{' '}
                <a href="/" style={styles.link} data-id="element-6720">
                  Return to home
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>;
  }
  // Error state
  return <div style={styles.pageWrapper} data-id="element-6721">
      <div style={styles.card} data-id="element-6722">
        <div style={styles.cardInner} data-id="element-6723">
          <div style={{
          textAlign: 'center'
        }} data-id="element-6724">
            <div style={{
            ...styles.iconCircle,
            backgroundColor: '#FEE2E2'
          }} data-id="element-6725">
              <AlertCircleIcon style={{
              width: '2rem',
              height: '2rem',
              color: '#DC2626'
            }} data-id="element-6726" />
            </div>
            <h2 style={styles.heading} data-id="element-6727">Verification Failed</h2>
            <p style={styles.subtitle} data-id="element-6728">
              We couldn't verify your email address.
            </p>
          </div>

          <div style={{
          marginTop: '1.5rem',
          padding: '1rem',
          backgroundColor: '#FEF2F2',
          borderLeft: '4px solid #EF4444',
          color: '#B91C1C',
          display: 'flex',
          alignItems: 'flex-start',
          borderRadius: '0 0.5rem 0.5rem 0'
        }} data-id="element-6729">
            <AlertCircleIcon style={{
            width: '1.25rem',
            height: '1.25rem',
            marginRight: '0.5rem',
            flexShrink: 0,
            marginTop: '2px'
          }} data-id="element-6730" />
            <p style={{
            margin: 0
          }} data-id="element-6731">
              {error}
            </p>
          </div>

          <a href="/teacher-login" style={{
          ...styles.primaryButton,
          marginTop: '1.5rem'
        }} data-id="element-6732">
            Go to Sign In
          </a>

          <div style={styles.footer} data-id="element-6733">
            <p style={{
            color: '#6B7280'
          }} data-id="element-6734">
              Need a new verification email? Sign in and request one from your
              dashboard.
            </p>
          </div>
        </div>
      </div>
    </div>;
};
// ============================================================
// PASSWORD RESET FLOW
// ============================================================
const PasswordResetFlow = ({
  oobCode
}: {
  oobCode: string;
}) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    label: 'Too Short',
    color: '#D1D5DB'
  });
  const [passwordError, setPasswordError] = useState('');
  useEffect(() => {
    setPasswordStrength(calculatePasswordStrength(password));
  }, [password]);
  useEffect(() => {
    if (confirmPassword && password !== confirmPassword) {
      setPasswordError('Passwords do not match');
    } else {
      setPasswordError('');
    }
  }, [password, confirmPassword]);
  const calculatePasswordStrength = (pwd: string) => {
    if (!pwd || pwd.length < 1) return {
      score: 0,
      label: 'Too Short',
      color: '#D1D5DB'
    };
    let score = 0;
    if (pwd.length >= 8) score += 1;
    if (pwd.length >= 12) score += 1;
    if (/[a-z]/.test(pwd)) score += 0.5;
    if (/[A-Z]/.test(pwd)) score += 0.5;
    if (/[0-9]/.test(pwd)) score += 1;
    if (/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+/.test(pwd)) score += 1;
    score = Math.min(4, score);
    const map = [{
      score: 0,
      label: 'Too Short',
      color: '#D1D5DB'
    }, {
      score: 1,
      label: 'Weak',
      color: '#EF4444'
    }, {
      score: 2,
      label: 'Fair',
      color: '#F97316'
    }, {
      score: 3,
      label: 'Good',
      color: '#EAB308'
    }, {
      score: 4,
      label: 'Strong',
      color: '#22C55E'
    }];
    return map[Math.floor(score)];
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    try {
      setIsLoading(true);
      console.log('[ResetPasswordPage] Confirming password reset...');
      await confirmPasswordReset(auth, oobCode, password);
      console.log('[ResetPasswordPage] Password reset successful!');
      setIsSuccess(true);
    } catch (err: any) {
      console.error('[ResetPasswordPage] Error:', err);
      if (err.code === 'auth/expired-action-code') {
        setError('This reset link has expired. Please request a new one.');
      } else if (err.code === 'auth/invalid-action-code') {
        setError('This reset link is invalid or has already been used.');
      } else if (err.code === 'auth/weak-password') {
        setError('Password is too weak. Please use a stronger password.');
      } else {
        setError('Failed to reset password. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };
  // Success state
  if (isSuccess) {
    return <div style={styles.pageWrapper} data-id="element-6735">
        <div style={styles.card} data-id="element-6736">
          <div style={styles.cardInner} data-id="element-6737">
            <div style={{
            textAlign: 'center'
          }} data-id="element-6738">
              <div style={{
              ...styles.iconCircle,
              backgroundColor: '#D1FAE5'
            }} data-id="element-6739">
                <CheckCircleIcon style={{
                width: '2rem',
                height: '2rem',
                color: '#059669'
              }} data-id="element-6740" />
              </div>
              <h2 style={styles.heading} data-id="element-6741">Password Reset!</h2>
              <p style={styles.subtitle} data-id="element-6742">
                Your password has been successfully changed.
              </p>
            </div>

            <div style={{
            marginTop: '1.5rem',
            padding: '1rem',
            backgroundColor: '#F0FDF4',
            borderLeft: '4px solid #22C55E',
            color: '#166534',
            display: 'flex',
            alignItems: 'flex-start',
            borderRadius: '0 0.5rem 0.5rem 0'
          }} data-id="element-6743">
              <CheckCircleIcon style={{
              width: '1.25rem',
              height: '1.25rem',
              marginRight: '0.5rem',
              flexShrink: 0,
              marginTop: '2px'
            }} data-id="element-6744" />
              <span data-id="element-6745">
                Your password has been successfully reset. You can now sign in
                with your new password.
              </span>
            </div>

            <a href="/teacher-login" style={{
            ...styles.primaryButton,
            marginTop: '1.5rem'
          }} data-id="element-6746">
              Sign In with New Password
            </a>
          </div>
        </div>
      </div>;
  }
  // Form state
  return <div style={styles.pageWrapper} data-id="element-6747">
      <div style={styles.card} data-id="element-6748">
        <div style={styles.cardInner} data-id="element-6749">
          <div style={{
          textAlign: 'center',
          marginBottom: '2rem'
        }} data-id="element-6750">
            <div style={{
            ...styles.iconCircle,
            backgroundColor: '#DBEAFE'
          }} data-id="element-6751">
              <LockIcon style={{
              width: '2rem',
              height: '2rem',
              color: '#2563EB'
            }} data-id="element-6752" />
            </div>
            <h2 style={styles.heading} data-id="element-6753">Create New Password</h2>
            <p style={styles.subtitle} data-id="element-6754">Enter your new password below</p>
          </div>

          {/* Error Alert */}
          {error && <div style={{
          marginBottom: '1.5rem',
          padding: '1rem',
          backgroundColor: '#FEF2F2',
          borderLeft: '4px solid #EF4444',
          color: '#B91C1C',
          display: 'flex',
          alignItems: 'flex-start',
          borderRadius: '0 0.5rem 0.5rem 0'
        }} data-id="element-6755">
              <AlertCircleIcon style={{
            width: '1.25rem',
            height: '1.25rem',
            marginRight: '0.5rem',
            flexShrink: 0,
            marginTop: '2px'
          }} data-id="element-6756" />
              <div data-id="element-6757">
                <p style={{
              margin: 0
            }} data-id="element-6758">
                  {error}
                </p>
                {(error.includes('expired') || error.includes('invalid')) && <a href="/forgot-password" style={{
              color: '#991B1B',
              fontWeight: '500',
              fontSize: '0.875rem',
              marginTop: '0.5rem',
              display: 'inline-block',
              textDecoration: 'underline'
            }} data-id="element-6759">
                    Request a new reset link
                  </a>}
              </div>
            </div>}

          <form onSubmit={handleSubmit} data-id="element-6760">
            {/* New Password */}
            <div style={{
            marginBottom: '1.5rem'
          }} data-id="element-6761">
              <label htmlFor="reset-password" style={styles.label} data-id="element-6762">
                New Password{' '}
                <span style={{
                color: '#EF4444'
              }} data-id="element-6763">
                  *
                </span>
              </label>
              <div style={{
              position: 'relative'
            }} data-id="element-6764">
                <input type={passwordVisible ? 'text' : 'password'} id="reset-password" value={password} onChange={e => setPassword(e.target.value)} style={styles.input} placeholder="Create a secure password (min. 8 characters)" required disabled={isLoading} data-id="element-6765" />
                <button type="button" style={styles.eyeButton} onClick={() => setPasswordVisible(!passwordVisible)} disabled={isLoading} data-id="element-6766">
                  {passwordVisible ? <EyeOffIcon style={styles.eyeIcon} data-id="element-6767" /> : <EyeIcon style={styles.eyeIcon} data-id="element-6768" />}
                </button>
              </div>
              {/* Strength bar */}
              <div style={{
              marginTop: '0.5rem'
            }} data-id="element-6769">
                <div style={{
                width: '100%',
                height: '0.5rem',
                backgroundColor: '#E5E7EB',
                borderRadius: '9999px',
                overflow: 'hidden'
              }} data-id="element-6770">
                  <div style={{
                  height: '100%',
                  width: `${passwordStrength.score / 4 * 100}%`,
                  backgroundColor: passwordStrength.color,
                  transition: 'width 0.3s, background-color 0.3s'
                }} data-id="element-6771" />
                </div>
                <div style={{
                marginTop: '0.25rem',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }} data-id="element-6772">
                  <span style={{
                  fontSize: '0.75rem',
                  fontWeight: '500',
                  color: passwordStrength.color
                }} data-id="element-6773">
                    {passwordStrength.label}
                  </span>
                  <span style={{
                  fontSize: '0.75rem',
                  color: '#6B7280'
                }} data-id="element-6774">
                    Use 8+ characters, include a number and symbol
                  </span>
                </div>
              </div>
            </div>

            {/* Confirm Password */}
            <div style={{
            marginBottom: '1.5rem'
          }} data-id="element-6775">
              <label htmlFor="reset-confirm-password" style={styles.label} data-id="element-6776">
                Confirm New Password{' '}
                <span style={{
                color: '#EF4444'
              }} data-id="element-6777">
                  *
                </span>
              </label>
              <div style={{
              position: 'relative'
            }} data-id="element-6778">
                <input type={confirmPasswordVisible ? 'text' : 'password'} id="reset-confirm-password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} style={{
                ...styles.input,
                borderColor: passwordError ? '#EF4444' : '#D1D5DB'
              }} placeholder="Confirm your new password" required disabled={isLoading} data-id="element-6779" />
                <button type="button" style={styles.eyeButton} onClick={() => setConfirmPasswordVisible(!confirmPasswordVisible)} disabled={isLoading} data-id="element-6780">
                  {confirmPasswordVisible ? <EyeOffIcon style={styles.eyeIcon} data-id="element-6781" /> : <EyeIcon style={styles.eyeIcon} data-id="element-6782" />}
                </button>
              </div>
              {passwordError && <p style={{
              marginTop: '0.25rem',
              fontSize: '0.875rem',
              color: '#EF4444'
            }} data-id="element-6783">
                  {passwordError}
                </p>}
            </div>

            {/* Submit */}
            <button type="submit" disabled={isLoading || !!passwordError} style={{
            ...styles.primaryButton,
            backgroundColor: isLoading || passwordError ? '#9CA3AF' : '#2563EB',
            cursor: isLoading || passwordError ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }} data-id="element-6784">
              {isLoading ? <>
                  <svg style={{
                animation: 'spin 1s linear infinite',
                marginRight: '0.75rem',
                width: '1.25rem',
                height: '1.25rem'
              }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-6785">
                    <circle style={{
                  opacity: 0.25
                }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-6786" />
                    <path style={{
                  opacity: 0.75
                }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-6787" />
                  </svg>
                  Resetting Password...
                </> : 'Reset Password'}
            </button>
          </form>

          <div style={styles.footer} data-id="element-6788">
            <p style={{
            color: '#6B7280'
          }} data-id="element-6789">
              Remember your password?{' '}
              <a href="/teacher-login" style={styles.link} data-id="element-6790">
                Sign in
              </a>
            </p>
          </div>
        </div>
      </div>
      <style data-id="element-6791">{`{spinnerKeyframes}`}</style>
    </div>;
};
// ============================================================
// INVALID LINK STATE
// ============================================================
const InvalidLinkState = ({
  mode
}: {
  mode: string | null;
}) => {
  return <div style={styles.pageWrapper} data-id="element-6792">
      <div style={styles.card} data-id="element-6793">
        <div style={styles.cardInner} data-id="element-6794">
          <div style={{
          textAlign: 'center'
        }} data-id="element-6795">
            <div style={{
            ...styles.iconCircle,
            backgroundColor: '#FEF3C7'
          }} data-id="element-6796">
              <AlertCircleIcon style={{
              width: '2rem',
              height: '2rem',
              color: '#D97706'
            }} data-id="element-6797" />
            </div>
            <h2 style={styles.heading} data-id="element-6798">Invalid Link</h2>
            <p style={styles.subtitle} data-id="element-6799">
              This link is invalid or missing required parameters.
            </p>
          </div>

          <div style={{
          marginTop: '1.5rem',
          padding: '1rem',
          backgroundColor: '#FFFBEB',
          borderLeft: '4px solid #F59E0B',
          color: '#92400E',
          display: 'flex',
          alignItems: 'flex-start',
          borderRadius: '0 0.5rem 0.5rem 0'
        }} data-id="element-6800">
            <AlertCircleIcon style={{
            width: '1.25rem',
            height: '1.25rem',
            marginRight: '0.5rem',
            flexShrink: 0,
            marginTop: '2px'
          }} data-id="element-6801" />
            <p style={{
            margin: 0
          }} data-id="element-6802">
              {mode ? `Unrecognized action type: "${mode}". The link may be corrupted.` : 'This link is missing required parameters. Please request a new one.'}
            </p>
          </div>

          <a href="/forgot-password" style={{
          ...styles.primaryButton,
          marginTop: '1.5rem'
        }} data-id="element-6803">
            Request New Link
          </a>

          <div style={styles.footer} data-id="element-6804">
            <p style={{
            color: '#6B7280'
          }} data-id="element-6805">
              <a href="/teacher-login" style={styles.link} data-id="element-6806">
                Go to Sign In
              </a>
              {' · '}
              <a href="/" style={styles.link} data-id="element-6807">
                Return to Home
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>;
};
// ============================================================
// MAIN COMPONENT — Routes based on mode param
// ============================================================
const ActionPageRouter = () => {
  const [searchParams] = useSearchParams();
  const oobCode = searchParams.get('oobCode');
  const mode = searchParams.get('mode');
  // DIAGNOSTIC: Log what mode we received
  useEffect(() => {
    console.log('[ResetPasswordPage] Component MOUNTED', {
      mode,
      hasOobCode: !!oobCode,
      url: window.location.href
    });
  }, []);
  // No oobCode = invalid link regardless of mode
  if (!oobCode) {
    return <InvalidLinkState mode={mode} data-id="element-6808" />;
  }
  // Route based on mode
  switch (mode) {
    case 'verifyEmail':
      return <EmailVerificationFlow oobCode={oobCode} data-id="element-6809" />;
    case 'resetPassword':
      return <PasswordResetFlow oobCode={oobCode} data-id="element-6810" />;
    default:
      // Unknown or missing mode
      return <InvalidLinkState mode={mode} data-id="element-6811" />;
  }
};
// Exported component with error boundary wrapper
export const ResetPasswordPage = () => {
  return <ActionPageErrorBoundary data-id="element-6812">
      <ActionPageRouter data-id="element-6813" />
    </ActionPageErrorBoundary>;
};
// ============================================================
// SHARED STYLES
// ============================================================
const spinnerKeyframes = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;
const styles: Record<string, React.CSSProperties> = {
  pageWrapper: {
    minHeight: '100vh',
    background: 'linear-gradient(to right, #0F1626, #1E3A8A, #2C3E50)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1rem 1rem 3rem 1rem'
  },
  card: {
    maxWidth: '28rem',
    width: '100%',
    backgroundColor: 'white',
    borderRadius: '0.75rem',
    overflow: 'hidden',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
  },
  cardInner: {
    padding: '2.5rem 2rem'
  },
  iconCircle: {
    margin: '0 auto 1rem auto',
    width: '4rem',
    height: '4rem',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  heading: {
    fontSize: '1.875rem',
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: '0.5rem'
  },
  subtitle: {
    color: '#6B7280',
    margin: 0
  },
  primaryButton: {
    display: 'inline-block',
    width: '100%',
    padding: '0.75rem 1rem',
    backgroundColor: '#2563EB',
    color: 'white',
    fontWeight: '500',
    borderRadius: '0.5rem',
    border: 'none',
    fontSize: '1rem',
    textDecoration: 'none',
    textAlign: 'center' as const,
    boxSizing: 'border-box' as const,
    cursor: 'pointer'
  },
  label: {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '0.25rem'
  },
  input: {
    width: '100%',
    padding: '0.75rem 2.5rem 0.75rem 1rem',
    border: '1px solid #D1D5DB',
    borderRadius: '0.5rem',
    fontSize: '1rem',
    color: '#111827',
    outline: 'none',
    boxSizing: 'border-box' as const
  },
  eyeButton: {
    position: 'absolute' as const,
    top: '50%',
    right: '0.75rem',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '0',
    color: '#6B7280'
  },
  eyeIcon: {
    width: '1.25rem',
    height: '1.25rem'
  },
  link: {
    color: '#2563EB',
    fontWeight: '500',
    textDecoration: 'none'
  },
  footer: {
    marginTop: '2rem',
    textAlign: 'center' as const
  }
};